import type { Request as HarRequest, Param } from 'har-format';
import type Oas from 'oas';
import type { Operation } from 'oas/operation';
import type { AuthForHAR, DataForHAR, OASDocument } from 'oas/types';

import oasToHar from '@readme/oas-to-har';
import sha256 from 'crypto-js/sha256';
import { useEffect, useState } from 'react';

import useLocalStorage from '@core/hooks/useLocalStorage';
import { fetcher } from '@core/hooks/useReadmeApi';
import { useProjectStore, useSuperHubStore } from '@core/store';

const CODE_SNIPPET_STORAGE_KEY = 'external-code-snippet';
class TemplateGenerator {
  private placeholderMap: Record<string, number | string> = {};

  private numericalPlaceholderCounter = 1234567890;

  private har: HarRequest;

  constructor(har: HarRequest) {
    this.har = structuredClone(har);
  }

  private generatePlaceholder(value: string, key: string): string {
    const placeholder = `readme_placeholder_${key}`.toLowerCase();
    this.placeholderMap[placeholder] = value;
    return placeholder;
  }

  private generateNumberPlaceholder(value: number): number {
    this.numericalPlaceholderCounter += 1;
    this.placeholderMap[String(this.numericalPlaceholderCounter)] = value;
    return this.numericalPlaceholderCounter;
  }

  private generateBooleanPlaceholder(value: boolean, key: string): boolean {
    const placeholder = `readme_placeholder_boolean_${key}_${value}`.toLowerCase();
    this.placeholderMap[placeholder] = value ? 'true' : 'false';
    return value;
  }

  public generatePathPlaceholder(value: string, index: number): string {
    const placeholder = `readme_placeholder_path_${index}`.toLowerCase();
    this.placeholderMap[placeholder] = value;
    return placeholder;
  }

  public replaceBasic(items: Param[], key: string): Param[] {
    if (!items) return items;
    return items.map(({ name, value }) => {
      if (!value) return { name, value };
      return {
        name,
        value: this.generatePlaceholder(value, `${key}_${name}`),
      };
    });
  }

  public replaceObject(item: unknown, key: string): unknown {
    let newItem: unknown;
    if (item === null || item === undefined) return item;

    if (typeof item === 'string') {
      newItem = this.generatePlaceholder(item, key);
    } else if (typeof item === 'number') {
      newItem = this.generateNumberPlaceholder(item);
    } else if (typeof item === 'boolean') {
      newItem = this.generateBooleanPlaceholder(item, key);
    } else if (typeof item === 'object') {
      if (Array.isArray(item)) {
        newItem = (item as unknown[]).map((v, i) => this.replaceObject(v, `${key}[${i}]`));
      } else {
        newItem = {};
        Object.keys(item).forEach(k => {
          (newItem as Record<string, unknown>)[k] = this.replaceObject(item[k], `${key}.${k}`);
        });
      }
    } else {
      newItem = item;
    }

    return newItem;
  }

  public replaceBasicValues() {
    ['headers', 'queryString', 'cookies'].forEach(key => {
      const items = this.har[key];
      if (!items) return;
      this.har[key] = this.replaceBasic(items, key);
    });
  }

  public replacePostData() {
    if (!this.har.postData) {
      return;
    }

    if (this.har.postData?.mimeType === 'application/json' && this.har.postData?.text) {
      try {
        this.har.postData.text = JSON.stringify(this.replaceObject(JSON.parse(this.har.postData.text), 'postdata'));
      } catch (e) {
        throw new Error(`Unable to create placeholder for JSON body: ${e}`);
      }
    }

    if (this.har.postData?.params) {
      this.har.postData.params = this.replaceBasic(this.har.postData.params, 'postdata');
    }
  }

  public replaceUrlValues(url: string, path: string) {
    const urlTemplate = `${url}${path}`.split('/');
    const originalUrl = this.har.url.split('/');
    const templateUrl: string[] = [];
    urlTemplate.forEach((param, index) => {
      let out = param;
      if (param !== originalUrl[index]) {
        out = this.generatePathPlaceholder(originalUrl[index], index);
      }
      templateUrl.push(out);
    });
    this.har.url = templateUrl.join('/');
  }

  public getPlaceholderMap(): Record<string, number | string> {
    return this.placeholderMap;
  }

  public getHar(): HarRequest {
    return this.har;
  }
}

function filterOasToMethod(api: OASDocument, path: string, method: string) {
  const oas = {
    ...api,
    paths: {
      [path]: {
        ...api.paths?.[path],
      },
    },
  };

  ['get', 'put', 'post', 'delete', 'options', 'head', 'patch', 'trace'].forEach(m => {
    if (m !== method) {
      delete oas.paths[path][m];
    }
  });

  return oas;
}

export function createTemplateValues(har: HarRequest, url: string, path: string) {
  const templateGenerator = new TemplateGenerator(har);
  templateGenerator.replaceBasicValues();
  templateGenerator.replacePostData();
  templateGenerator.replaceUrlValues(url, path);

  const outHar = templateGenerator.getHar();
  const placeholderMap = templateGenerator.getPlaceholderMap();
  return { placeholderMap, har: outHar };
}

export function hydrateTemplate(str: string, placeholderMap: Record<string, number | string>): string {
  let result = str;
  Object.entries(placeholderMap).forEach(([placeholder, value]) => {
    const stringValue = String(value);
    // there should only be one instances of the placeholder, do not replace all instances
    result = result.replace(placeholder, stringValue);
  });
  return result;
}

interface CodeRetrieverOutput {
  code: string;
  install?: string;
  language: string;
}

export type CodeRetriever = (args: {
  har: HarRequest;
  language: string;
  oas: OASDocument;
}) => Promise<CodeRetrieverOutput>;

function createBackendCodeRetriever(url) {
  return async ({ har, oas, language }: { har: HarRequest; oas: OASDocument; language: string }) => {
    const response = await fetcher<{ data: CodeRetrieverOutput }>(`${url}/integrations/external-code-snippet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ har, schema: oas, language }),
    });

    return response.data;
  };
}

function createKey({
  placeholderHar,
  language,
  projectId,
  operation,
}: {
  placeholderHar: HarRequest;
  language: string;
  projectId: string;
  operation: OASDocument;
}) {
  return sha256(JSON.stringify({ placeholderHar, operation, projectId, language })).toString();
}
export type CodeSnippetCache = Record<
  string,
  {
    code: string;
    createdAt: number;
  }
>;

export function removeExpired(cache: CodeSnippetCache) {
  const dayAgo = Date.now() - 1000 * 60 * 60 * 24;
  Object.keys(cache).forEach(key => {
    if (cache[key].createdAt < dayAgo) {
      delete cache[key];
    }
  });
}

interface UseExternalSnippetProps {
  apiDefinition: Oas;
  auth?: AuthForHAR;
  codeRetriever?: CodeRetriever;
  enabled?: boolean;
  formData: DataForHAR;
  language?: string;
  operation: Operation;
}

export default function useExternalSnippet({
  apiDefinition,
  auth,
  formData,
  operation,
  language,
  codeRetriever,
  enabled = false,
}: UseExternalSnippetProps) {
  const [snippet, setSnippet] = useState<string | null>(null);
  const [install, setInstall] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getApiBaseUrl = useSuperHubStore(s => s.getApiBaseUrl);
  const [placeholderSnippet, setTemplateSnippet] = useState<string>();
  const [placeholderKey, setPlaceholderKey] = useState<string>();
  const storage = useLocalStorage({ json: true });
  const projectId = useProjectStore(s => s.data.id);

  useEffect(() => {
    if (!enabled) return;
    if (!language) return;
    if (isLoading) return;
    if (!operation.api?.paths?.[operation.path]?.[operation.method]) return;

    const oas = filterOasToMethod(operation.api, operation.path, operation.method);
    const getCodeSnippet = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const inputHar = oasToHar(apiDefinition, operation, formData, auth);
        const har = inputHar.log.entries[0].request;
        const url = apiDefinition.url();
        const { har: placeholderHar, placeholderMap } = createTemplateValues(har, url, operation.path);
        const key = createKey({ placeholderHar, language, projectId, operation: operation.api });

        if (placeholderKey === key && placeholderSnippet) {
          const hydratedCode = hydrateTemplate(placeholderSnippet, placeholderMap);
          setSnippet(hydratedCode);
          return;
        }

        const cache: CodeSnippetCache = storage.getItem(CODE_SNIPPET_STORAGE_KEY) || {};
        removeExpired(cache);
        const cachedSnippet = cache?.[key];

        if (cachedSnippet) {
          const hydratedCode = hydrateTemplate(cachedSnippet.code, placeholderMap);
          setTemplateSnippet(cachedSnippet.code);
          setSnippet(hydratedCode);
          return;
        }

        setPlaceholderKey(key);

        const codeRetrieverFunc = codeRetriever || createBackendCodeRetriever(getApiBaseUrl({ branch: null }));
        const data = await codeRetrieverFunc({ har: placeholderHar, oas, language });

        const hydratedCode = hydrateTemplate(data.code, placeholderMap);

        cache[key] = {
          code: data.code,
          createdAt: Date.now(),
        };
        storage.setItem(CODE_SNIPPET_STORAGE_KEY, cache);
        setTemplateSnippet(data.code);
        setSnippet(hydratedCode);
        setInstall(data.install || '');
      } catch (err) {
        // TODO: figure out why setting error causes this hook to infinitely re-run
        // setError('unable to generate code snippet');
        if (err.status === 406) {
          setTemplateSnippet('no SDK for this language');
          setSnippet('no SDK for this language');
          return;
        }
        setTemplateSnippet('unable to generate code snippet');
        setSnippet('unable to generate code snippet');
      } finally {
        setIsLoading(false);
      }
    };

    getCodeSnippet();
  }, [
    apiDefinition,
    auth,
    codeRetriever,
    enabled,
    formData,
    getApiBaseUrl,
    isLoading,
    language,
    operation,
    placeholderKey,
    placeholderSnippet,
    projectId,
    storage,
  ]);

  return {
    code: snippet,
    install,
    isLoading,
    error,
  };
}
